import type { SiteDocument } from '../../../types/api';

/**
 * Which of a site's documents an inspector needs on the ground (Jim
 * 2026-10-07): "all the Inspector needs access to is the BMP Map and Site
 * Notices... the updated and the original BMP Maps."
 *
 * Titles come from the legacy file share ("BMP Map - McDonald's 12-345.pdf",
 * "GC - TCEQ Large Site Notice - ...") and from the inspection gate, which
 * mirrors each monthly sheet as "BMP Map — 2026-10 (Sheet 2)". Appendix N
 * "...BMPs" manuals are not maps and stay out.
 */
const SITE_NOTICE_TYPES = new Set([
  'notice',
  'owner_site_notice',
  'gc_site_notice',
]);

export type InspectorDocKind = 'bmp_map' | 'site_notice';

export const GATE_MIRROR_DESCRIPTION =
  'Monthly BMP-map photo from the inspection gate.';
const GATE_TITLE = /^bmp map — (\d{4}-\d{2})/i;

type DocLike = Pick<
  SiteDocument,
  'title' | 'file_name' | 'document_type' | 'description' | 'inspection_id'
>;

/** "2026-10" for a sheet captured at the inspection gate, else null. */
export function gatePeriod(doc: DocLike): string | null {
  const m = GATE_TITLE.exec(doc.title ?? '');
  if (m) return m[1];
  return doc.description === GATE_MIRROR_DESCRIPTION ? '' : null;
}

export function inspectorDocKind(doc: DocLike): InspectorDocKind | null {
  if (doc.inspection_id) return null;
  const text = `${doc.title ?? ''} ${doc.file_name ?? ''}`.toLowerCase();
  if (/\bbmp[\s_-]?map/.test(text) || doc.document_type === 'bmp_map') {
    return 'bmp_map';
  }
  // Owner/GC Site Notice (Jim 2026-10-16) counts like the plain Notice type
  if (
    /\bsite[\s_-]?notice/.test(text) ||
    SITE_NOTICE_TYPES.has(String(doc.document_type))
  ) {
    return 'site_notice';
  }
  return null;
}

export function pickInspectorDocuments(docs: SiteDocument[]): {
  bmpMaps: SiteDocument[];
  siteNotices: SiteDocument[];
} {
  const bmpMaps: SiteDocument[] = [];
  const siteNotices: SiteDocument[] = [];
  for (const d of docs) {
    const kind = inspectorDocKind(d);
    if (kind === 'bmp_map') bmpMaps.push(d);
    else if (kind === 'site_notice') siteNotices.push(d);
  }
  // Updated maps first (newest month on top), then the originals, newest
  // upload first — the map as it stands today is what the inspector wants.
  bmpMaps.sort((a, b) => {
    const pa = gatePeriod(a);
    const pb = gatePeriod(b);
    if ((pa !== null) !== (pb !== null)) return pa !== null ? -1 : 1;
    if (pa !== null && pb !== null && pa !== pb) return pa < pb ? 1 : -1;
    return (b.created_at ?? '').localeCompare(a.created_at ?? '');
  });
  siteNotices.sort((a, b) =>
    (b.created_at ?? '').localeCompare(a.created_at ?? '')
  );
  return { bmpMaps, siteNotices };
}
